import type { HebrewDayInfo } from "./calendar";
import type { ZmanimEvaluator } from "./evaluate";
import type { Anchor, Rounding } from "./schema";
import { addDays, MS_PER_MINUTE } from "./types";

export interface TimedEvent {
  ms: number;
  rounding: Rounding;
}

export interface SpecialTimesOptions {
  /** Minutes before sunset, by the location's custom. */
  candleLightingMinutes: number;
  /** The anchors chosen by the user's profile. */
  sunset: Anchor;
  alos: Anchor;
  tzeit: Anchor;
  /** End of Shabbat and Yom Tov (default 8.5°) and the Rabbeinu Tam time shown beside it. */
  shabbatEnd: Anchor;
  rabbeinuTam: Anchor;
}

export interface SpecialTimes {
  candleLighting: (TimedEvent & { afterNightfall: boolean; minutes: number }) | null;
  shabbatEnd: { time: TimedEvent; rabbeinuTam: TimedEvent } | null;
  fast: { start: TimedEvent; end: TimedEvent } | null;
}

/** Candle lighting, havdala and fast times for one day. Missing astronomical events yield null. */
export function specialTimes(
  evaluator: ZmanimEvaluator,
  info: HebrewDayInfo,
  options: SpecialTimesOptions,
): SpecialTimes {
  const { date } = info;
  const at = (anchor: Anchor, day = date) => evaluator.evaluate(anchor, day);

  let candleLighting: SpecialTimes["candleLighting"] = null;
  if (info.candleLightingTonight) {
    if (info.candleLightingAfterNightfall) {
      const ms = at(options.shabbatEnd);
      if (ms !== null) candleLighting = { ms, rounding: "ceil", afterNightfall: true, minutes: 0 };
    } else {
      const sunset = at(options.sunset);
      if (sunset !== null) {
        candleLighting = {
          ms: sunset - options.candleLightingMinutes * MS_PER_MINUTE,
          rounding: "floor",
          afterNightfall: false,
          minutes: options.candleLightingMinutes,
        };
      }
    }
  }

  let shabbatEnd: SpecialTimes["shabbatEnd"] = null;
  if (info.endsTonight) {
    const end = at(options.shabbatEnd);
    const rt = at(options.rabbeinuTam);
    if (end !== null && rt !== null) {
      shabbatEnd = {
        time: { ms: end, rounding: "ceil" },
        rabbeinuTam: { ms: rt, rounding: "ceil" },
      };
    }
  }

  let fast: SpecialTimes["fast"] = null;
  if (info.fast) {
    const start = info.fast.startsPreviousEvening
      ? at(options.sunset, addDays(date, -1))
      : at(options.alos);
    // Yom Kippur ends like Shabbat; other fasts end at the profile's nightfall.
    const end = at(info.isShabbatOrYomTov ? options.shabbatEnd : options.tzeit);
    if (start !== null && end !== null) {
      fast = { start: { ms: start, rounding: "floor" }, end: { ms: end, rounding: "ceil" } };
    }
  }

  return { candleLighting, shabbatEnd, fast };
}
